import React, {useRef, useState} from "react";
import {DateRange, ArrowDropDown, ChevronRight, ChevronLeft, CheckRounded, Close} from '@mui/icons-material';
import DateRangeCalendar from "./DateRangeCalendar";
import {formatDateRange} from "./dateRangeUtils";
import {
    Box,
    Button,
    ButtonBase,
    MenuItem,
    TextField,
    IconButton,
    Popover,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {alpha} from '@mui/material/styles';
import {getSettingsColors} from '../settings/SettingsDesign';
import {dateRangePopoverStyles, dateRangeFooterStyles, dateRangeMenuProps, DateRangeSelection} from './DateRangePickerDesign';
import moment from 'moment-timezone';
import {compose} from "redux";
import {connect} from "react-redux";
import TimezoneSelect from "../timezone/TimezoneSelect";
import {useForm} from "react-hook-form";
import ReactHookFormSelect from "../input/ReactHookFormSelect";
import {endHours, startHours} from "./DataList";
import {timezones} from "../timezone/TimezonesList";
import {authActions} from "../../store/actions/authAction";

export function AirbnbDateRangePicker(props) {
    const theme = useTheme();
    const {dateLabel, onChangeDate, auth: {userSettings: {timezone}}, hideTimezoneSelect = false, compact = false, hideDayNavigation = false, iconOnly = false, triggerSx = []} = props;

    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [focusedInput, setFocusedInput] = useState("startDate");
    const [open, setOpen] = useState(null);

    const {handleSubmit, control, setValue, reset} = useForm({defaultValues: {
        startTime: moment(props.from).format('HH:mm'), endTime: moment(props.to).format('HH:mm'),
        timezone: timezones.find(item => item.timezone === timezone) || {timezone, offset: timezone}
    }});
    const [rangeError, setRangeError] = useState('');
    const popoverActions = useRef(null);

    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const compactCalendar = useMediaQuery(theme.breakpoints.down(960));

    const dateRange = [
        {
            label: 'Today',
            startDate: moment().tz(timezone).startOf("d"),
            endDate: moment().tz(timezone).endOf("d")
        },
        {
            label: 'Yesterday',
            startDate: moment().tz(timezone).subtract(1, 'day').startOf("d"),
            endDate: moment().tz(timezone).subtract(1, 'day').endOf("d")
        },
        {
            label: 'Last 3 Days',
            startDate: moment().tz(timezone).subtract(2, 'day').startOf("d"),
            endDate: moment().tz(timezone).startOf("d")
        },
        {
            label: 'Last 7 Days',
            startDate: moment().tz(timezone).subtract(6, 'day').startOf("d"),
            endDate: moment().tz(timezone).startOf("d")
        },
        {
            label: 'This Week',
            startDate: moment().tz(timezone).startOf("isoWeek"),
            endDate: moment().tz(timezone).endOf("isoWeek")
        },
        {
            label: 'Last Week',
            startDate: moment().tz(timezone).startOf("isoWeek").subtract(1, 'week'),
            endDate: moment().tz(timezone).endOf("isoWeek").subtract(1, 'week')
        },
        {
            label: 'This Month',
            startDate: moment().tz(timezone).startOf("M"),
            endDate: moment().tz(timezone).endOf("d")
        },
        {
            label: 'Last Month',
            startDate: moment().tz(timezone).subtract(1, 'month').startOf("M"),
            endDate: moment().tz(timezone).subtract(1, 'month').endOf("M")
        },
        {
            label: 'Last 3 Months',
            startDate: moment().tz(timezone).subtract(3, 'month').startOf("d"),
            endDate: moment().tz(timezone).endOf("d")
        },
        {
            label: 'Last 6 Months',
            startDate: moment().tz(timezone).subtract(6, 'month').startOf("d"),
            endDate: moment().tz(timezone).endOf("d")
        },
        {
            label: 'Last 12 Months',
            startDate: moment().tz(timezone).subtract(12, 'month').startOf("d"),
            endDate: moment().tz(timezone).endOf("d")
        },
    ];

    const applyRange = (start, end, label, startTime = moment(props.from).format('HH:mm'), endTime = moment(props.to).format('HH:mm'), beforeChange) => {
        const range = formatDateRange(start, end, startTime, endTime);
        if (!range) {
            setRangeError('End date and time must be after the start date and time.');
            return false;
        }
        beforeChange?.();
        onChangeDate(range.from, range.to, label);
        setRangeError('');
        setOpen(null);
        return true;
    };

    const handleDateRangeChange = ({startDate: start, endDate: end}) => {
        setStartDate(start);
        setEndDate(end);
        setRangeError('');
        if (start && end) applyRange(start, end, '');
    };

    const handlePresetClick = (start, end, label) => applyRange(start, end, label);
    const shiftRange = offset => applyRange(moment(props.from).add(offset, 'day'), moment(props.to).add(offset, 'day'), '');

    const handleOnClickApplyTimezoneAndTime = data => {
        applyRange(startDate, endDate, '', data.startTime, data.endTime, () => {
            if (!hideTimezoneSelect) props.updateTimezone(data.timezone.timezone);
        });
    };

    const handleOpen = event => {
        setStartDate(moment(props.from));
        setEndDate(moment(props.to));
        setFocusedInput('startDate');
        setRangeError('');
        reset({startTime: moment(props.from).format('HH:mm'), endTime: moment(props.to).format('HH:mm'),
            timezone: timezones.find(item => item.timezone === timezone) || {timezone, offset: timezone}});
        setOpen({anchorEl: event.currentTarget});
    };

    // Keep externally supplied minutes selectable, even outside the hourly presets.
    const timeOptions = (options, value) => options.some(option => option.value === value) ? options : [...options, {label: value, value}].sort((a, b) => a.value.localeCompare(b.value));
    const rangeLabel = dateLabel !== undefined && dateLabel !== '' ? dateLabel : moment(props?.from).format('D MMM YYYY') + ' – ' + moment(props?.to).format('D MMM YYYY');

    return (
        <Box sx={{flexShrink: compact ? 1 : 0, minWidth: 0, color: "text.secondary", display: "flex", paddingRight: compact || iconOnly ? 0 : 1, ...(compact && {flex: "1 1 auto"}), ...(iconOnly && {flex: '0 0 auto'})}}>
            <Box display="flex" flexDirection="row" sx={{minWidth: 0, ...(compact && {width: "100%"}), ...(iconOnly && {alignItems: 'center'})}}>
                <Box sx={{minWidth: 0, ...(compact && {flex: 1}), ...(iconOnly && {display: 'flex', alignItems: 'center'})}}>
                    {iconOnly ? <Tooltip title={rangeLabel}><IconButton id="airbnb-calendar" aria-label="Choose reporting period" aria-haspopup="dialog"
                        aria-expanded={Boolean(open)} onClick={handleOpen} sx={[{width: 36, height: 36, borderRadius: '8px', color: 'text.secondary',
                            bgcolor: theme => getSettingsColors(theme).inset,
                            '&:hover': {color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1)},
                            '& .MuiSvgIcon-root': {fontSize: 20}}, ...(Array.isArray(triggerSx) ? triggerSx : [triggerSx])]}><DateRange/></IconButton></Tooltip> : <Button
                        id="airbnb-calendar"
                        onClick={handleOpen}
                        startIcon={<DateRange/>}
                        endIcon={<ArrowDropDown/>}
                        color="inherit"
                        size="medium"
                        aria-label="Choose reporting period"
                        sx={[{textTransform: "none", whiteSpace: "nowrap", color: "text.primary", ...(compact && {height: 36, width: "100%", minWidth: 0, maxWidth: 300, fontSize: 13, borderRadius: "8px", border: 0, bgcolor: theme => getSettingsColors(theme).inset, px: 1.25, "& .MuiButton-startIcon .MuiSvgIcon-root": {fontSize: 19}})}, ...(Array.isArray(triggerSx) ? triggerSx : [triggerSx])]}
                    >
                        <Box component="span" sx={{minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}}>{rangeLabel}</Box>
                    </Button>}
                </Box>
                {!hideDayNavigation && <><Box pt={iconOnly ? 0 : "3px"} pl={0.5} sx={iconOnly ? {display: 'flex', alignItems: 'center'} : undefined}>
                    <Tooltip title="Previous day" placement="top">
                        <Box component="span" sx={iconOnly ? {display: 'flex'} : undefined}>
                            <IconButton
                                color="inherit"
                                onClick={() => shiftRange(-1)}
                                aria-label="previous-day"
                                size="small"
                            >
                                <ChevronLeft color="inherit"/>
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
                <Box pt={iconOnly ? 0 : "3px"} sx={iconOnly ? {display: 'flex', alignItems: 'center'} : undefined}>
                    <Tooltip title="Next day" placement="top">
                        <Box component="span" sx={iconOnly ? {display: 'flex'} : undefined}>
                            <IconButton
                                color="inherit"
                                onClick={() => shiftRange(1)}
                                aria-label="next-day"
                                size="small"
                                disabled={moment(props.from).format("YYYY-MM-DD") >= moment().tz(timezone).format("YYYY-MM-DD")}
                                sx={{":disabled": {color: "text.disabledDark"}}}
                            >
                                <ChevronRight color="inherit"/>
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box></>}
            </Box>

            <Popover
                id="date-range-picker-airbnb"
                action={popoverActions}
                marginThreshold={8}
                slotProps={{transition: {onEntered: () => popoverActions.current?.updatePosition()}, paper: {sx: dateRangePopoverStyles}}}
                open={Boolean(open?.anchorEl)}
                anchorEl={open?.anchorEl}
                onClose={() => setOpen(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{width: mobile ? 304 : compactCalendar ? 524 : 824, maxWidth: '100%'}}>
                    <Box sx={{display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '164px minmax(0, 1fr)',
                        gap: 2, px: {xs: 2, sm: 2.5}, py: 2, alignItems: 'start'}}>
                        {mobile ? <TextField select id="date-range-preset" size="small"
                            sx={{'&& .MuiOutlinedInput-root': {bgcolor: theme => theme.palette.mode === 'light' ? getSettingsColors(theme).inset : getSettingsColors(theme).input}}}
                            value={dateRange.some(range => range.label === dateLabel) ? dateLabel : ''}
                            onChange={event => {
                                const preset = dateRange.find(range => range.label === event.target.value);
                                if (preset) handlePresetClick(preset.startDate, preset.endDate, preset.label);
                            }}
                            slotProps={{
                                select: {displayEmpty: true, inputProps: {'aria-label': 'Quick ranges'}, MenuProps: dateRangeMenuProps}
                            }}>
                            <MenuItem value="" disabled>Choose a preset</MenuItem>
                            {dateRange.map(range => <MenuItem key={range.label} value={range.label}>{range.label}</MenuItem>)}
                        </TextField> : <Box component="nav" aria-label="Predefined date ranges" sx={{p: 1.25, borderRadius: '12px', bgcolor: theme => getSettingsColors(theme).inset}}>
                            <Box sx={{display: 'grid', gap: 0.25}}>
                                {dateRange.map(range => <ButtonBase key={range.label} data-date-preset={range.label}
                                    aria-pressed={dateLabel === range.label} onClick={() => handlePresetClick(range.startDate, range.endDate, range.label)}
                                    sx={{display: 'flex', justifyContent: 'space-between', gap: 0.5, width: '100%', minHeight: 36, px: 1.25,
                                        textAlign: 'left', borderRadius: '8px', fontSize: 12,
                                        fontWeight: dateLabel === range.label ? 600 : 400,
                                        color: dateLabel === range.label ? 'primary.main' : 'text.secondary',
                                        bgcolor: theme => dateLabel === range.label ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                        '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.07), color: 'text.primary'},
                                        '&.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main'}}}>
                                    {range.label}{dateLabel === range.label && <CheckRounded sx={{fontSize: 16}}/>}
                                </ButtonBase>)}
                            </Box>
                        </Box>}
                        <Box sx={{minWidth: 0, display: 'grid', gap: 2}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <DateRangeSelection startDate={startDate} endDate={endDate} focusedInput={focusedInput} onFocusChange={setFocusedInput}/>
                                </Box>
                                <IconButton onClick={() => setOpen(null)} aria-label="Close date picker" size="small" sx={{color: 'text.secondary'}}>
                                    <Close fontSize="small"/>
                                </IconButton>
                            </Box>
                            <DateRangeCalendar
                                minDate={moment('2021-03-01')} today={moment().tz(timezone)}
                                numberOfMonths={compactCalendar ? 1 : 2} daySize={mobile ? 38 : 40}
                                startDate={startDate} endDate={endDate} onDatesChange={handleDateRangeChange}
                                focusedInput={focusedInput} onFocusChange={setFocusedInput}/>
                            {rangeError && <Typography role="alert" color="error" sx={{fontSize: 12}}>{rangeError}</Typography>}
                        </Box>
                    </Box>
                    {hideTimezoneSelect ? <Box sx={{px: 2.5, py: 1.25, bgcolor: theme => getSettingsColors(theme).inset,
                        color: 'text.secondary', fontSize: 12}}>Timezone: {timezone}</Box> :
                        <Box sx={{...dateRangeFooterStyles, display: 'grid', gridTemplateColumns: {xs: 'repeat(2, minmax(0, 1fr)) auto', sm: 'minmax(0, 1fr) 100px 100px auto'},
                            alignItems: 'center'}}>
                            <Box sx={{minWidth: 0, gridColumn: {xs: '1 / -1', sm: 'auto'}}}>
                                <TimezoneSelect setValue={setValue} control={control} selectedValue={timezone} inputName="timezone" inputLabelShrink
                                    slotProps={{paper: {sx: {bgcolor: theme => getSettingsColors(theme).card, borderRadius: '12px'}}}}
                                    style={{'& .MuiAutocomplete-input': {fontSize: mobile ? 16 : 13, minHeight: 24, padding: '0px 6px !important'}}}/>
                            </Box>
                            <ReactHookFormSelect setValue={setValue} control={control} label="Start time" name="startTime"
                                options={timeOptions(startHours(), moment(props.from).format('HH:mm'))} defaultValue={moment(props.from).format('HH:mm')}
                                style={{'& .MuiSelect-select': {fontSize: mobile ? 16 : 13, py: 0.75, pl: 1, pr: '26px !important'}}}/>
                            <ReactHookFormSelect setValue={setValue} control={control} label="End time" name="endTime"
                                options={timeOptions(endHours(), moment(props.to).format('HH:mm'))} defaultValue={moment(props.to).format('HH:mm')}
                                style={{'& .MuiSelect-select': {fontSize: mobile ? 16 : 13, py: 0.75, pl: 1, pr: '26px !important'}}}/>
                            <Button onClick={handleSubmit(handleOnClickApplyTimezoneAndTime)} variant="contained"
                                sx={{px: 1.5}}>Apply</Button>
                        </Box>
                    }
                </Box>
            </Popover>
        </Box>
    );
}

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    }
};

const actionCreators = {
    updateTimezone: authActions.updateTimezone,
};

export default compose(connect(mapStateToProps, actionCreators))(AirbnbDateRangePicker);
